import { debounce } from 'lodash';

import { queryClient } from '@/query/client';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import type { MeetingCircles } from '@littlebird/ui-utils';

const MEETING_CIRCLES_CACHE_PREFIX = 'meetings-circles-v9:';
const MEETING_CIRCLES_REFRESH_MS = 1_500;
const MEETING_CIRCLES_REFRESH_MAX_WAIT_MS = 10_000;

const meetingCirclesVersions = new Map<string, string>();

const refreshMeetingCircles = debounce(
  (accountId: string) => {
    void queryClient.invalidateQueries({
      queryKey: ['meetings', 'circles', accountId],
    });
  },
  MEETING_CIRCLES_REFRESH_MS,
  { maxWait: MEETING_CIRCLES_REFRESH_MAX_WAIT_MS }
);

const cachedMeetingCircles = new Map<string, MeetingCircles | null>();

export const refreshMeetingCirclesIfChanged = (
  accountId: string,
  version: string
) => {
  const previous = meetingCirclesVersions.get(accountId);
  meetingCirclesVersions.set(accountId, version);
  if (previous !== undefined && previous !== version) {
    refreshMeetingCircles(accountId);
  }
};

export const readCachedMeetingCircles = (accountId: string) => {
  if (!cachedMeetingCircles.has(accountId)) {
    cachedMeetingCircles.set(
      accountId,
      getLocalStorageItem(MEETING_CIRCLES_CACHE_PREFIX + accountId)
    );
  }
  return cachedMeetingCircles.get(accountId) ?? undefined;
};

export const writeCachedMeetingCircles = (
  accountId: string,
  circles: MeetingCircles
) => {
  cachedMeetingCircles.set(accountId, circles);
  try {
    setLocalStorageItem(MEETING_CIRCLES_CACHE_PREFIX + accountId, circles);
  } catch {
    return;
  }
};

/** Call on logout. Otherwise the account's next session refreshes the circles it has just loaded */
export const resetMeetingCircles = () => {
  refreshMeetingCircles.cancel();
  meetingCirclesVersions.clear();
  cachedMeetingCircles.clear();
};
